import { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Card, CircularProgress, Grid, Chip, Alert,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Snackbar, useMediaQuery, useTheme,
} from '@mui/material';
import { Message, Phone, Email as EmailIcon, PersonOutlined, AttachFile } from '@mui/icons-material';
import axios from 'axios';
import { uploadAttachment } from '../components/AttachmentsPanel';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function ParentClassTeachers() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageDlg, setMessageDlg] = useState(null);
  const [msgBody, setMsgBody] = useState('');
  const [msgFile, setMsgFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [snack, setSnack] = useState(null); // { severity, text }

  useEffect(() => {
    const load = async () => {
      try {
        const lRes = await axios.get(`${API}/api/guardians/me/learners`, { headers: hdrs() });
        const kids = lRes.data?.data || [];
        setLearners(kids);
        if (kids.length > 0) {
          const l = kids[0];
          setSelectedLearner(l);
          loadTeachers(l.admNo);
        }
      } catch (e) {
        console.error('Error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user.email]);

  const loadTeachers = async (admNo) => {
    try {
      const res = await axios.get(`${API}/api/academics/learner/${admNo}/class-teachers`, { headers: hdrs() });
      setTeachers(res.data || []);
    } catch (e) {
      console.error('Error loading teachers:', e);
      setTeachers([]);
    }
  };

  const sendMessage = async () => {
    if (!msgBody.trim()) return;
    setSending(true);
    try {
      const teacherName = messageDlg?.name;
      const r = await axios.post(`${API}/api/messages`, {
        to: messageDlg.email,
        subject: `Message from parent of ${selectedLearner?.name}`,
        body: msgBody,
        learnerAdmNo: selectedLearner?.admNo,
      }, { headers: hdrs() });
      if (msgFile && r.data?._id) {
        try { await uploadAttachment(msgFile, 'Message', r.data._id); }
        catch { setSnack({ severity: 'warning', text: 'Message sent, but the attachment failed to upload' }); }
      }
      setMsgBody('');
      setMsgFile(null);
      setMessageDlg(null);
      setSnack(s => s?.severity === 'warning' ? s : { severity: 'success', text: `Message sent to ${teacherName} ✓` });
    } catch (e) {
      setSnack({ severity: 'error', text: 'Failed to send message: ' + (e.response?.data?.error || e.message) });
    } finally {
      setSending(false);
    }
  };

  if (loading) return <Box textAlign="center" py={8}><CircularProgress /></Box>;

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box py={{ xs: 2, sm: 3 }}>
        <Typography variant="h5" fontWeight={700} mb={3} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>👨‍🏫 Class Teachers</Typography>

        <Alert severity="info" sx={{ mb: 3, fontSize: { xs: '0.8rem', sm: '1rem' } }}>
          Contact your child's class teacher. Use the message button to send inquiries.
        </Alert>

        {/* Learner Selector */}
        {learners.length > 1 && (
          <Box sx={{ mb: 3, display: 'flex', gap: 1, overflowX: 'auto', pb: 1, WebkitOverflowScrolling: 'touch' }}>
            {learners.map(l => (
              <Chip
                key={l._id}
                label={l.name}
                onClick={() => {
                  setSelectedLearner(l);
                  loadTeachers(l.admNo);
                }}
                color={selectedLearner?._id === l._id ? 'primary' : 'default'}
                variant={selectedLearner?._id === l._id ? 'filled' : 'outlined'}
                sx={{ flexShrink: 0 }}
              />
            ))}
          </Box>
        )}

        {/* Teachers Grid */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {teachers.length === 0 ? (
            <Grid item xs={12}>
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <PersonOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No class teachers assigned yet</Typography>
              </Card>
            </Grid>
          ) : (
            teachers.map((teacher, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={{
                  p: { xs: 2, sm: 3 }, textAlign: 'center', height: '100%', transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
                }}>
                  {/* Avatar */}
                  <Box sx={{
                    width: 80, height: 80, margin: '0 auto 1rem',
                    borderRadius: '50%', bgcolor: 'primary.main', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 28, fontWeight: 700,
                  }}>
                    {teacher.name?.charAt(0).toUpperCase() || '?'}
                  </Box>

                  {/* Name & Role */}
                  <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                    {teacher.name || 'Unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                    {teacher.role || 'Class Teacher'}
                  </Typography>

                  {/* Subject */}
                  {teacher.subject && (
                    <Chip
                      label={teacher.subject}
                      size="small"
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                  )}

                  {/* Contact Info */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    {teacher.email && (
                      <Box display="flex" alignItems="center" gap={1} justifyContent="center" minWidth={0}>
                        <EmailIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                          {teacher.email}
                        </Typography>
                      </Box>
                    )}
                    {teacher.phone && (
                      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
                        <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {teacher.phone}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Message Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    startIcon={<Message />}
                    onClick={() => setMessageDlg(teacher)}
                    sx={{ textTransform: 'none' }}
                  >
                    Send Message
                  </Button>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        {/* Message Dialog */}
        <Dialog open={Boolean(messageDlg)} onClose={() => setMessageDlg(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle>
            Send Message to {messageDlg?.name}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                RE: {selectedLearner?.name}
              </Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={5}
              label="Your message"
              placeholder="Type your message here..."
              value={msgBody}
              onChange={e => setMsgBody(e.target.value)}
              variant="outlined"
            />
            <Box display="flex" alignItems="center" gap={1} mt={1.5}>
              <Button component="label" size="small" startIcon={<AttachFile />} sx={{ textTransform: 'none' }}>
                Attach file
                <input type="file" hidden onChange={e => setMsgFile(e.target.files?.[0] || null)} />
              </Button>
              {msgFile && <Chip label={msgFile.name} size="small" onDelete={() => setMsgFile(null)} />}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Keep it professional and respectful.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMessageDlg(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={sendMessage}
              disabled={sending || !msgBody.trim()}
              startIcon={<Message />}
            >
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Toast */}
        <Snackbar
          open={Boolean(snack)}
          autoHideDuration={4000}
          onClose={() => setSnack(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snack?.severity || 'info'} variant="filled"
            onClose={() => setSnack(null)} sx={{ borderRadius: 2, boxShadow: 3 }}>
            {snack?.text}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}
